import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/auth/Login.vue'),
      meta: { guest: true },
    },
    {
      path: '/',
      component: () => import('@/components/layout/MainLayout.vue'),
      meta: { requiresAuth: true },
      children: [
        {
          path: '',
          name: 'dashboard',
          component: () => import('@/views/dashboard/Dashboard.vue'),
        },
        {
          path: 'calendar',
          name: 'calendar',
          component: () => import('@/views/calendar/Calendar.vue'),
        },
        {
          // /bookings page was merged into /orders; redirect for ~30 days for muscle memory.
          path: 'bookings',
          redirect: '/orders',
        },
        {
          path: 'operator-booking',
          name: 'operator-booking',
          component: () => import('@/views/bookings/OperatorBooking.vue'),
        },
        {
          path: 'customers',
          name: 'customers',
          component: () => import('@/views/customers/CustomerList.vue'),
        },
        {
          path: 'customers/:id',
          name: 'customer-detail',
          component: () => import('@/views/customers/CustomerDetail.vue'),
        },
        {
          path: 'orders',
          name: 'orders',
          component: () => import('@/views/orders/OrderList.vue'),
        },
        {
          path: 'cashbox',
          name: 'cashbox',
          component: () => import('@/views/cashbox/CashboxView.vue'),
        },
        {
          path: 'pos',
          name: 'pos',
          component: () => import('@/views/pos/POSPage.vue'),
        },
        {
          path: 'cables',
          name: 'cables',
          component: () => import('@/views/cables/CableList.vue'),
        },
        {
          path: 'services',
          name: 'services',
          component: () => import('@/views/services/ServiceList.vue'),
        },
        {
          path: 'group-schedules',
          name: 'group-schedules',
          component: () => import('@/views/group-schedules/GroupScheduleList.vue'),
        },
        {
          path: 'staff',
          name: 'staff',
          component: () => import('@/views/staff/StaffList.vue'),
        },
        {
          path: 'promo-codes',
          name: 'promo-codes',
          component: () => import('@/views/promo-codes/PromoCodeList.vue'),
        },
        {
          path: 'promo-codes/:id/stats',
          name: 'promo-code-stats',
          component: () => import('@/views/promo-codes/PromoCodeStats.vue'),
        },
        {
          path: 'subscription-plans',
          name: 'subscription-plans',
          component: () => import('@/views/subscription-plans/SubscriptionPlanList.vue'),
        },
        {
          path: 'gift-certificates',
          name: 'gift-certificates',
          component: () => import('@/views/gift-certificates/GiftCertificateList.vue'),
        },
        {
          path: 'subscriptions',
          name: 'subscriptions',
          component: () => import('@/views/subscriptions/SubscriptionList.vue'),
        },
        {
          path: 'subscription-requests',
          name: 'subscription-requests',
          component: () => import('@/views/subscription-requests/SubscriptionRequestList.vue'),
        },
        {
          path: 'subscriptions/:id',
          name: 'subscription-detail',
          component: () => import('@/views/subscriptions/SubscriptionDetail.vue'),
        },
        {
          path: 'reports',
          name: 'reports',
          component: () => import('@/views/reports/ReportsView.vue'),
        },
        {
          path: 'customer-analytics',
          name: 'customer-analytics',
          component: () => import('@/views/analytics/CustomerAnalytics.vue'),
        },
        {
          path: 'help',
          name: 'help',
          component: () => import('@/views/help/HelpPage.vue'),
        },
        {
          path: 'special-dates',
          name: 'special-dates',
          component: () => import('@/views/special-dates/SpecialDates.vue'),
        },
        {
          path: 'settings',
          name: 'settings',
          component: () => import('@/views/settings/Settings.vue'),
        },
        {
          path: 'settings/bots',
          name: 'settings-bots',
          component: () => import('@/views/settings/Bots.vue'),
        },
        {
          path: 'settings/document-types',
          name: 'settings-document-types',
          component: () => import('@/views/settings/DocumentTypes.vue'),
        },
        {
          path: 'settings/widget-live-status',
          name: 'settings-widget-live-status',
          component: () => import('@/views/settings/WidgetLiveStatus.vue'),
        },
        {
          path: 'marketing/broadcasts',
          name: 'marketing-broadcasts',
          component: () => import('@/views/marketing/BroadcastList.vue'),
        },
        {
          path: 'marketing/broadcasts/:id',
          name: 'marketing-broadcast-editor',
          component: () => import('@/views/marketing/BroadcastEditor.vue'),
        },
        {
          path: 'equipment',
          name: 'equipment',
          component: () => import('@/views/equipment/EquipmentList.vue'),
        },
        {
          path: 'equipment/rentals',
          name: 'equipment-rentals',
          component: () => import('@/views/equipment/EquipmentRentals.vue'),
        },
        {
          path: 'expenses',
          name: 'expenses',
          component: () => import('@/views/expenses/ExpenseList.vue'),
        },
        {
          path: 'owners',
          name: 'owners',
          component: () => import('@/views/owners/OwnerList.vue'),
        },
        {
          path: 'owners/dashboard',
          name: 'owners-dashboard',
          component: () => import('@/views/owners/OwnerDashboard.vue'),
        },
        {
          path: 'owners/:id',
          name: 'owner-detail',
          component: () => import('@/views/owners/OwnerDetail.vue'),
        },
      ],
    },
    // Standalone operator panel (role operator_panel) — own minimal,
    // mobile-first layout, not the admin MainLayout.
    {
      path: '/operator',
      name: 'operator-panel',
      component: () => import('@/views/operator/OperatorPanel.vue'),
      meta: { requiresAuth: true },
    },
    // Super Admin Routes
    {
      path: '/superadmin',
      component: () => import('@/components/layout/MainLayout.vue'),
      meta: { requiresAuth: true, requiresSuperAdmin: true },
      children: [
        {
          path: '',
          name: 'superadmin-dashboard',
          component: () => import('@/views/superadmin/SuperAdminDashboard.vue'),
        },
        {
          path: 'tenants',
          name: 'superadmin-tenants',
          component: () => import('@/views/superadmin/TenantList.vue'),
        },
        {
          path: 'tenants/:id',
          name: 'superadmin-tenant-detail',
          component: () => import('@/views/superadmin/TenantDetail.vue'),
        },
        {
          path: 'bot-billing',
          name: 'superadmin-bot-billing',
          component: () => import('@/views/superadmin/BotBilling.vue'),
        },
        {
          path: 'pos-monitor',
          name: 'superadmin-pos-monitor',
          component: () => import('@/views/superadmin/PosMonitor.vue'),
        },
        {
          path: 'ai-usage',
          name: 'superadmin-ai-usage',
          component: () => import('@/views/superadmin/AiUsage.vue'),
        },
        {
          path: 'document-review',
          name: 'superadmin-document-review',
          component: () => import('@/views/superadmin/DocumentReview.vue'),
        },
      ],
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: () => import('@/views/NotFound.vue'),
    },
  ],
  // Always land at the top on navigation (preserve the saved position only on
  // browser back/forward). The page scrolls the window — the layout root grows
  // past the viewport — so this targets the right scroller.
  scrollBehavior(_to, _from, savedPosition) {
    return savedPosition ?? { top: 0, left: 0 }
  },
})

// Navigation guards
router.beforeEach(async (to, _from, next) => {
  const authStore = useAuthStore()

  // Wait for the persisted token to be exchanged for a real user before
  // deciding. On F5 the token is read from localStorage synchronously, but
  // user is loaded async via /auth/me — without awaiting, isAuthenticated
  // is false on the very first navigation and the guard kicks the user
  // back to /login despite a valid session.
  await authStore.init()

  // Panel-only roles (operator_panel, instructor) are locked to their single
  // screen — they have no admin permissions, so any other authenticated page
  // would just 403 on load.
  const isPanelOnly = authStore.isPanelOnly

  // Customers have no access to the admin app at all. If a customer token ever
  // lands here (e.g. reused from the widget cabinet), sign it out and bounce to
  // login rather than render an admin shell that 403s on every request.
  if (authStore.isAuthenticated && authStore.isCustomer) {
    await authStore.logout()
    next({ name: 'login' })
    return
  }

  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    next({ name: 'login' })
  } else if (to.meta.guest && authStore.isAuthenticated) {
    next(isPanelOnly ? { name: 'operator-panel' } : { name: 'dashboard' })
  } else if (authStore.isAuthenticated && isPanelOnly && to.name !== 'operator-panel') {
    next({ name: 'operator-panel' })
  } else if (to.meta.requiresSuperAdmin && !authStore.isSuperAdmin) {
    next({ name: 'dashboard' })
  } else {
    next()
  }
})

export default router
